<template>
  <div class="flex h-screen bg-white font-roboto">
    <!-- Sidebar -->
    <div
      :class="[
        'transition-all duration-300',
        isSidebarShrunk ? 'w-16' : 'w-72',
      ]"
      class="relative bg-custom-blue text-white flex flex-col"
    >
      <!-- Logo / toggle -->
      <div class="flex items-center justify-between px-6 py-3">
        <div v-if="!isSidebarShrunk" class="flex items-center">
          <img
            class="object-cover w-18 h-10"
            src="@/assets/images/Jiji-Health-White-Logo.png"
            alt="Your avatar"
          />
        </div>
        <div>
          <button @click="toggleSidebar" class="text-white focus:outline-none">
            <svg
              class="w-6 h-6 text-gray-100 dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="M5 7h14M5 12h14M5 17h14"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Main nav items (flex-1 so they push the bottom section down) -->
      <nav class="flex-1 overflow-y-auto">
        <div v-for="tab in mainSidebarTabs" :key="tab.name">
          <router-link
            v-if="!tab.children"
            :to="tab.route"
            :class="[
              isActive(tab) ? activeClass : '',
              'flex items-center px-4 py-3 m-2 duration-200 text-white rounded-md',
            ]"
          >
            <div v-html="tab.icon" class="w-6 h-6"></div>
            <span v-if="!isSidebarShrunk" class="ml-4">{{ tab.name }}</span>
          </router-link>

          <!-- Dropdown tabs -->
          <div v-else>
            <div
              class="flex items-center px-4 py-3 m-2 duration-200 text-white rounded-md cursor-pointer"
              @click="toggleDropdown(tab)"
            >
              <div v-html="tab.icon" class="w-6 h-6"></div>
              <span v-if="!isSidebarShrunk" class="ml-4">{{ tab.name }}</span>
            </div>
            <div
              v-if="isDropdownOpen(tab)"
              :class="[isSidebarShrunk ? 'ml-0' : 'ml-8']"
            >
              <router-link
                v-for="child in tab.children"
                :key="child.name"
                :to="child.route"
                :class="[
                  isActive(child) ? activeClass : '',
                  'flex items-center px-4 py-2 m-2 duration-200 text-white rounded-md',
                ]"
              >
                <div v-html="child.icon" class="w-5 h-5"></div>
                <span v-if="!isSidebarShrunk" class="ml-4">{{
                  child.name
                }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </nav>

      <!-- Bottom-pinned nav items (e.g. Settings) -->
      <div class="border-t border-white/20">
        <router-link
          v-for="tab in bottomSidebarTabs"
          :key="tab.name"
          :to="tab.route"
          :class="[
            isActive(tab) ? activeClass : '',
            'flex items-center px-4 py-3 m-2 duration-200 text-white rounded-md',
          ]"
        >
          <div v-html="tab.icon" class="w-6 h-6"></div>
          <span v-if="!isSidebarShrunk" class="ml-4">{{ tab.name }}</span>
        </router-link>
      </div>
    </div>

    <!-- Main content -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <Header />
      <main class="flex-1 overflow-x-hidden overflow-y-auto bg-white">
        <div v-if="billingStatus.show_warning || billingStatus.suspended"
            :class="billingStatus.suspended ? 'bg-red-100' : 'bg-blue-100'"
            class="flex items-center mx-auto rounded-lg p-4 w-2/3">
          <span v-if="billingStatus.billing_model === 'per_visit'">
            <template v-if="billingStatus.suspended">Your account is suspended. Outstanding bill of KES {{ billingStatus.accumulated_bill_amount }} has reached your limit of KES {{ billingStatus.maximum_bill_amount }}. Other staff cannot log in until this is settled.</template>
            <template v-else>Your account has a pending system bill. Please submit a payment of KES {{ billingStatus.accumulated_bill_amount }} or at least 80% of the bill to avoid account suspension for other roles in your application.</template>
          </span>
          <span v-else>
            <template v-if="billingStatus.suspended">
              Your account is suspended due to unpaid monthly invoice(s): KES {{ billingStatus.total_outstanding_amount }} outstanding
              ({{ billingStatus.outstanding_bills?.map(b => `${b.month}: KES ${b.amount}`).join(', ') }}).
              Other staff cannot log in until this is settled.
            </template>
            <template v-else>Your fixed monthly bill of KES {{ billingStatus.fixed_monthly_amount }} is due. Please settle it before month end to avoid account suspension for other roles in your application.</template>
          </span>
          <button class="bg-green-400 text-white p-1" @click.prevent="togglePaymentModal">Pay Now</button>
        </div>
        <div class="mx-auto px-6 py-2">
          <slot />
        </div>
      </main>
    </div>
  </div>

  <SystemBillingPaymentModal
    v-if="billingStatus.billing_model === 'per_visit'"
    :show="showPaymentModal"
    :amount="billingStatus.accumulated_bill_amount || 0"
    :facilityId="facility?.id || null"
    @close="togglePaymentModal"
  />
  <FixedMonthlyPaymentModal
    v-else
    :show="showPaymentModal"
    :amount="billingStatus.accumulated_bill_amount"
    :facilityId="facility?.id || null"
    @close="togglePaymentModal"
 />
</template>

<script>
import sidebarTabs from "@/config/adminSidebar";
import Header from "../HeaderLayout.vue"
import apiClient from "@/services/api";
import SystemBillingPaymentModal from "@/views/modals/system-billing/SystemBillingPaymentModal.vue";
import FixedMonthlyPaymentModal from "@/views/modals/system-billing/FixedMonthlyPaymentModal.vue";

export default {
  name: "SidebarLayout",
  components: {
    SystemBillingPaymentModal,
    FixedMonthlyPaymentModal,
    Header
  },
  data() {
    return {
      sidebarTabs,
      activeClass: "bg-custom-yellow",
      openDropdown: null,
      isSidebarShrunk: false,
      facility: { id: null },
      billingStatus: { suspended: false, show_warning: false, billing_model: 'per_visit' },
      showPaymentModal: false
    };
  },
  computed: {
    filteredSidebarTabs() {
      const user = localStorage.getItem("user");
      if (user) {
        const parsedUser = JSON.parse(user);
        if (parsedUser && parsedUser.UserType) {
          if (parsedUser.UserType === "Nurse") {
            return this.sidebarTabs.filter((tab) => tab.name !== "Patients");
          }
        }
      }
      return this.sidebarTabs;
    },

    mainSidebarTabs() {
      return this.filteredSidebarTabs.filter((tab) => !tab.pinBottom);
    },
    bottomSidebarTabs() {
      return this.filteredSidebarTabs.filter((tab) => tab.pinBottom);
    },
  },
  mounted() {
    this.getUserType();
    this.getBillingStatus();
  },
  methods: {
    toggleSidebar() {
      this.isSidebarShrunk = !this.isSidebarShrunk;
    },
    isActive(tab) {
      return this.$route.path === tab.route;
    },
    toggleDropdown(tab) {
      this.openDropdown = this.openDropdown === tab ? null : tab;
    },
    isDropdownOpen(tab) {
      return this.openDropdown === tab;
    },
    getUserType() {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.UserType) {
        this.userType = user.UserType;
      }
    },
    async getFacility() {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.FacilityId) {
        this.$toast.error("Session User or Facility Id not found.");
        return;
      }
      try {
        const response = await apiClient.fetchFacility(user.FacilityId);
        if (response.success) {
          this.facility = {
            id: response.data.FacilityId,
            accumulated_bill_amount: parseFloat(response.data.accumulated_bill_amount),
            maximum_bill_amount: parseFloat(response.data.maximum_bill_amount)
          };
        } else {
          this.$toast.error("Failed to fetch facility");
        }
      } catch (error) {
        this.$toast.error("Request to Fetch Facility Failed. Please check your network connection.");
        console.log(error);
      }
    },
    togglePaymentModal() {
      this.showPaymentModal = !this.showPaymentModal;
    },

    async getBillingStatus() {
      const user = JSON.parse(localStorage.getItem("user"))

      if (!user || !user.FacilityId) {
        this.$toast.error("User or Facility Id not found.")
        return;
      }
      this.facility.id = user.FacilityId;

      try {
        const response = await apiClient.fetchBillingStatus(user.FacilityId)
  
        if (response.success) {
          this.billingStatus = response.data;
        } else {
          this.$toast.error("Fetch to fetch billing status")
        }

      } catch(error) {
        this.$toast.error(error.message || "Request to fetch billing status failed. Please check your internet connection.")
      }
    },
  },
};
</script>